import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfigurationService } from "../configuration/configuration.service";

export interface SignatureNameConfig {
  includeMiddleName?: boolean;
  includePrefix?: boolean;
  includeSuffix?: boolean;
  normalizeApostrophes?: boolean;
  trimSpaces?: boolean;
}

export interface UserNameComponents {
  prefix?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
}

@Injectable()
export class SignatureProvider {
  private baseUrl: string;
  private defaultNameConfig: SignatureNameConfig = {
    includeMiddleName: true,
    includePrefix: false,
    includeSuffix: false,
    normalizeApostrophes: true,
    trimSpaces: true,
  };

  constructor(public http: HttpClient, private config: ConfigurationService) {
    this.baseUrl = config.getConfig().signatureUrl;
  }

  SignAdmissionReport(body) {
    const url = `${this.baseUrl}/signatures/`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.http.post(url, body, { headers });
  }

  SignDischargeReport(body) {
    const url = `${this.baseUrl}/signatures/`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    return this.http.post(url, body, { headers });
  }

  /**
   * Standardizes user name for signature validation
   * @param user - User object or name components
   * @param config - Optional configuration to override defaults
   */
  getStandardizedSignatureName(
    user: any | UserNameComponents,
    config?: Partial<SignatureNameConfig>
  ): string {
    const nameConfig = { ...this.defaultNameConfig, ...config };
    const components = this.extractNameComponents(user);

    return this.formatNameForSignature(components, nameConfig);
  }

  /**
   * Validates typed signature against standardized name
   */
  validateSignature(
    typedSignature: string,
    user: any | UserNameComponents,
    config?: Partial<SignatureNameConfig>
  ): boolean {
    if (!typedSignature?.trim()) return false;

    const standardizedName = this.getStandardizedSignatureName(user, config);
    const normalizedTyped = this.normalizeSignatureInput(typedSignature);
    const normalizedStandard = this.normalizeSignatureInput(standardizedName);

    return normalizedTyped === normalizedStandard;
  }

  private extractNameComponents(user: any): UserNameComponents {
    // Handle different user object structures
    if (user.firstName && user.lastName) {
      return {
        prefix: user.prefix,
        firstName: user.firstName,
        middleName: user.middleName || user.middleInitial,
        lastName: user.lastName,
        suffix: user.suffix,
      };
    }

    // Fallback: parse from user.name if available
    if (user.name) {
      return this.parseFullName(user.name);
    }

    throw new Error("Invalid user object: missing required name fields");
  }

  private formatNameForSignature(
    components: UserNameComponents,
    config: SignatureNameConfig
  ): string {
    const parts: string[] = [];

    if (config.includePrefix && components.prefix) {
      parts.push(components.prefix);
    }

    parts.push(components.firstName);

    if (config.includeMiddleName && components.middleName) {
      parts.push(components.middleName);
    }

    parts.push(components.lastName);

    if (config.includeSuffix && components.suffix) {
      parts.push(components.suffix);
    }

    let result = parts.join(" ");

    if (config.normalizeApostrophes) {
      result = result.replace(/'/g, "'");
    }

    if (config.trimSpaces) {
      result = result.replace(/\s+/g, " ").trim();
    }

    return result;
  }

  private normalizeSignatureInput(input: string): string {
    return input
      .replace(/'/g, "'") // Normalize apostrophes
      .replace(/\s+/g, " ") // Normalize spaces
      .trim()
      .toLowerCase();
  }

  private parseFullName(fullName: string): UserNameComponents {
    // Basic name parsing logic - can be enhanced
    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 2) {
      return { firstName: parts[0], lastName: parts[1] };
    } else if (parts.length === 3) {
      return { firstName: parts[0], middleName: parts[1], lastName: parts[2] };
    }

    // Handle more complex cases as needed
    return { firstName: parts[0], lastName: parts[parts.length - 1] };
  }
}



// WEBPACK FOOTER //
// ./src/providers/signature-service/signature-service.ts